/* eslint semi:0 */
import { once, makeSafeFn } from '../../utils/fn'
import { nodes, isVisible } from '../../utils/dom'
import { capitalizeFirstLetter } from '../../utils/string'
import { info, warn } from '../../application/console'
import { getFormFactor, path as pagePath, domainFromFqdn } from '../../utils/ua'
import { SELECTORS } from '../../config/preRenderedSelectorsConfig'
import { getState, setState, addReducer, subscribe } from '../../application/appStore'

import {
  getIsTv,
  getIsSlideshow,
  isSupportedForDomain,
  getHasBottomWidgetIndicator,
  postTemplate,
} from '../../application/pageDataObject'
import { queryString, addOnLandscapeChangeEvent, removeMatchMediaEvent } from '../../utils/window'
import {
  locale,
  getShortLocale,
  AVAILABLE_LOCALES,
} from '../../providers/i18nProvider/i18nProvider'

const { isEmbedded, isNativeApp } = pagePath
const IS_TABOOLA_ENABLED = true
const IS_TABOOLA_ENABLED_IN_WEBVIEW = true
const { TABOOLA_WIDGET_CONTAINER } = SELECTORS

const TABOOLA_MASTER_DOMAIN = '90min'
const TABOOLA_ROOT_PUBLISHER_ID = '90min'
const CURRENT_DOMAIN = domainFromFqdn()
const { EN, TH, ES, VN, IT, ID, FR, DE, BR, TR } = AVAILABLE_LOCALES
const TABOOLA_ENABLED_LOCALES = {
  '90min': [EN, TH, ES, VN, IT, ID, FR, DE, TR, BR],
  floor8: [EN, ES],
  '12up': [EN, ES],
  dbltap: [EN],
  esporteinterativo: [BR],
  pluralist: [EN],
  german_site: [DE],
}

const { noTaboola, debugTaboola, delayTaboolaBySeconds } = queryString

function isEnabledInWebView() {
  if (isNativeApp && CURRENT_DOMAIN !== 'esporteinterativo') {
    return IS_TABOOLA_ENABLED_IN_WEBVIEW
  }
  return true
}

function isLocaleInCurrentDomain() {
  return TABOOLA_ENABLED_LOCALES.hasOwnProperty(CURRENT_DOMAIN)
    ? TABOOLA_ENABLED_LOCALES[CURRENT_DOMAIN].includes(locale)
    : false
}
function shouldLoadTaboola() {
  return (
    IS_TABOOLA_ENABLED &&
    !noTaboola &&
    !isEmbedded &&
    isSupportedForDomain('Taboola') &&
    isEnabledInWebView() &&
    isLocaleInCurrentDomain()
  )
}

function getTaboolaPublisherIdByLocale(localeCode = getShortLocale()) {
  const isDefaultPublisherId = CURRENT_DOMAIN === TABOOLA_MASTER_DOMAIN && localeCode === EN
  return isDefaultPublisherId
    ? TABOOLA_ROOT_PUBLISHER_ID
    : `${TABOOLA_ROOT_PUBLISHER_ID}-${CURRENT_DOMAIN}-${localeCode}`
}

function getMinuteMediaScript() {
  let taboolaScript =
    postTemplate === 'article' && getShortLocale() !== 'br'
      ? `https://cdn.taboola.com/libtrc/90min-network/loader.js`
      : `https://cdn.taboola.com/libtrc/${getTaboolaPublisherIdByLocale()}/loader.js`
  return taboolaScript
}

function getTaboolaScriptUrlByLocale() {
  let script =
    CURRENT_DOMAIN !== 'esporteinterativo'
      ? getMinuteMediaScript()
      : 'https://cdn.taboola.com/libtrc/esporteinterativo/loader.js'
  return script
}

function taboolaGlobalInit() {
  return (window._taboola = window._taboola || []) // eslint-disable-line no-extra-parens
}

function pushMessagesToTaboola(...messages) {
  debugTaboola && info('pushMessagesToTaboola:', messages)
  makeSafeFn(taboolaGlobalInit).invoke().push(...messages)
  return messages
}

function getTaboolaWidgetConfFromElementMetadata(element) {
  const { id, dataset: { placement, modeId: mode } } = element
  return {
    mode,
    container: id,
    placement: `${placement} ${postTemplate}`,
  }
}

const TABOOLA_PAGE_TYPES = {
  PHOTO: 'photo',
  VIDEO: 'video',
  SEARCH: 'search',
  ARTICLE: 'article',
  CATEGORY: 'category',
}

function getTaboolaPageType() {
  const { VIDEO, ARTICLE, PHOTO } = TABOOLA_PAGE_TYPES
  if (getHasBottomWidgetIndicator()) {
    return VIDEO
  }
  if (getIsTv()) {
    return VIDEO
  }
  if (getIsSlideshow()) {
    return PHOTO
  }
  return ARTICLE
}

const TABOOLA_MESSAGES = {
  PAGE_TYPE_SETUP: {
    // 3.3 in API documentation
    target_type: 'mix', // eslint-disable-line camelcase
    [getTaboolaPageType()]: 'auto',
    video_source: 'brightcove', // eslint-disable-line camelcase
  },
  REFRESH_RECOMMENDATIONS: {
    notify: 'newPageLoad',
  },
  FLUSH_WIDGETS: {
    flush: true,
  },
}

function checkTaboolaElementInViewport(opts) {
  const { taboolaElement } = opts
  const isTaboolaElementVisibleOnOrientationChanged = isVisible(taboolaElement)
  if (isTaboolaElementVisibleOnOrientationChanged) {
    pushMessagesToTaboola(
      getTaboolaWidgetConfFromElementMetadata(taboolaElement),
      TABOOLA_MESSAGES.FLUSH_WIDGETS
    )
    removeMatchMediaEvent(opts)
  }
}

const TABOOLA_INITIAL_STATE = {
  widgets: [],
}

const TABOOLA_ACTION_TYPES = {
  REFRESH_RECOMMENDATIONS: 'TABOOLA_REFRESH_RECOMMENDATIONS',
}

const TABOOLA_STATE_KEY = 'taboola'

function taboolaReducer(state = TABOOLA_INITIAL_STATE, action) {
  // info('taboolaReducer', action.type, action)
  switch (action.type) {
    case TABOOLA_ACTION_TYPES.REFRESH_RECOMMENDATIONS:
      return setState(state, action.state)
    default:
  }
  return state
}

const {
  PAGE_TYPE_SETUP: FIRST_MESSAGE,
  FLUSH_WIDGETS: LAST_MESSAGE,
  REFRESH_RECOMMENDATIONS,
} = TABOOLA_MESSAGES

const SLIDESHOW_COUNT_TO_REFRESH_TABOOLA_RECOMMENDATIONS = 3

function shouldRefreshRecommendations(state = {}) {
  const { slideshow } = state
  return (
    slideshow &&
    slideshow.changedSlidesCount % SLIDESHOW_COUNT_TO_REFRESH_TABOOLA_RECOMMENDATIONS === 0
  )
}

function collectWidgetMessagesFromDom() {
  const widgetMessages = []
  const taboolaContainers = nodes(TABOOLA_WIDGET_CONTAINER)
  if (!taboolaContainers || !taboolaContainers.length) {
    debugTaboola && info('no taboola containers found: ', { taboolaContainers })
    return widgetMessages
  }
  for (const taboolaElement of taboolaContainers) {
    const isTaboolaElementVisible = isVisible(taboolaElement)
    if (isTaboolaElementVisible) {
      debugTaboola && info('visible:', taboolaElement)
      widgetMessages.push(getTaboolaWidgetConfFromElementMetadata(taboolaElement))
    } else {
      const opts = { taboolaElement }
      const mqlAndHandlerObj = addOnLandscapeChangeEvent(checkTaboolaElementInViewport.part(opts))
      Object.assign(opts, mqlAndHandlerObj)
      debugTaboola && info('hidden:', taboolaElement, { opts })
    }
  }
  return widgetMessages
}

function initTaboolaWidgets(widgetMessages) {
  if (widgetMessages.length) {
    return pushMessagesToTaboola(FIRST_MESSAGE, ...widgetMessages, LAST_MESSAGE)
  }
  return widgetMessages
}

export function reloadTaboolaWidgets(widgetMessages) {
  debugTaboola && warn('reloadTaboolaWidgets', widgetMessages)
  if (widgetMessages.length) {
    pushMessagesToTaboola(REFRESH_RECOMMENDATIONS)
    initTaboolaWidgets(widgetMessages)
    return true
  }
  return false
}

export const widgetMessages = []

const refresh = once(reloadTaboolaWidgets.part(widgetMessages))

let isRefreshing = false

function stateListener(state = getState()) {
  if (!isRefreshing) {
    isRefreshing = true
    if (shouldRefreshRecommendations(state)) {
      refresh()
    } else {
      refresh.reset()
    }
    isRefreshing = false
  }
}

function setupTaboola() {
  if (delayTaboolaBySeconds) {
    info(`Loading taboola after ${delayTaboolaBySeconds} seconds.`)
  }
  addReducer(TABOOLA_STATE_KEY, taboolaReducer)
  subscribe(stateListener)
  widgetMessages.push(...collectWidgetMessagesFromDom())
  debugTaboola && info(widgetMessages)
  initTaboolaWidgets(widgetMessages)
}

function loadTaboolaWithDelay(secondsToDelay = delayTaboolaBySeconds) {
  warn(`Delaying taboola loading by ${secondsToDelay} seconds.`)
  return setTimeout(setupTaboola, secondsToDelay * 1e3)
}

export const taboolaScriptConfig = {
  src: getTaboolaScriptUrlByLocale,
  beforeLoad: shouldLoadTaboola,
  onload: delayTaboolaBySeconds ? loadTaboolaWithDelay : setupTaboola,
}



// WEBPACK FOOTER //
// ./provolution/app/views/provolution/jsCore/organizedByType/config/taboolaScriptConfig.js